/**
 * Speed Compliance Report
 * Hastighetsefterlevnadsrapport (Uppsala krav 5.4.3.2)
 */

import React from 'react';
import { useTranslation } from '@/store/languageStore';
import {
  ChartBarIcon,
  ArrowDownTrayIcon,
  TruckIcon,
  ExclamationTriangleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
} from 'recharts';
import { useSpeedCompliance } from '@/hooks/useSpeedCompliance';
import type { TabType } from '@/types/speedCompliance';
import type { SpeedComplianceStats, VehicleSpeedStats, SpeedSegment } from '@/types/speedCompliance';
import { getViolationColor, getViolationRate, formatDuration } from '@/types/speedCompliance';
import OrgTreeSelect from '@/components/common/OrgTreeSelect';
import DateRangeFilter from '@/components/common/DateRangeFilter';
import { DataTable, EmptyState, ReportPageLayout, LoadingSpinner, LoadingState, SummaryCard } from '@/components/common';

const SpeedComplianceReport: React.FC = () => {
  const { t } = useTranslation();
  const {
    loading,
    activeTab,
    setActiveTab,
    selectedOrg,
    setSelectedOrg,
    dateRange,
    setDateRange,
    summary,
    orgStats,
    vehicleStats,
    trendData,
    speedSegments,
    segmentsSummary,
    dataLoaded,
    apiMessage,
    fetchData,
    exportToExcel,
  } = useSpeedCompliance();

  return (
    <ReportPageLayout
      title={t('speedCompliance.title') || 'Hastighetsefterlevnad'}
      description={t('speedCompliance.description') || 'Analysera hastighetsefterlevnad per organisation och fordon'}
      icon={<ChartBarIcon className="w-6 h-6 text-primary" />}
      actions={
        <button
          onClick={exportToExcel}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
        >
          <ArrowDownTrayIcon className="w-5 h-5" />
          Exportera Excel
        </button>
      }
      message={
        apiMessage ? (
          <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 flex items-start gap-3">
            <ExclamationTriangleIcon className="w-6 h-6 text-amber-600 dark:text-amber-500 shrink-0 mt-0.5" />
            <p className="text-amber-800 dark:text-amber-200 text-sm">{apiMessage}</p>
          </div>
        ) : undefined
      }
      filters={
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-textSecondary mb-1">
              {t('common.organization') || 'Organisation'}
            </label>
            <OrgTreeSelect
              value={selectedOrg}
              onChange={setSelectedOrg}
              placeholder={t('common.selectOrganization') || 'Välj organisation'}
            />
          </div>

          <DateRangeFilter
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onChange={(field, value) =>
              setDateRange((prev) => ({ ...prev, [field]: value }))
            }
          />

          <div className="flex items-end">
            <button
              onClick={fetchData}
              disabled={loading || !selectedOrg}
              className="w-full px-4 py-2 bg-bgPrimary hover:bg-bgSecondary disabled:bg-border text-textLight font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <LoadingSpinner size="sm" variant="white" className="shrink-0" />
                  <span>Hämtar...</span>
                </>
              ) : (
                <>
                  <ChartBarIcon className="w-5 h-5" />
                  <span>Hämta data</span>
                </>
              )}
            </button>
          </div>
        </div>
      }
      padding="compact"
      variant="container"
      minHeight="full"
      filtersVariant="elevated"
    >
      {/* Instructions when no data loaded */}
      {!dataLoaded && !loading && (
        <EmptyState
          variant="primary"
          icon={<ChartBarIcon />}
          title={t('speedComplianceReport.selectOrgAndDateRange')}
          description={'Klicka på "Hämta data" för att visa hastighetsöverträdelser.'}
          minHeight="min-h-0"
        />
      )}

      {/* Summary Cards */}
      {dataLoaded && summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <SummaryCard
            variant="elevated"
            title={t('speedComplianceReport.violationPercent')}
            value={`${getViolationRate(summary.avgComplianceRate)}%`}
            icon={<ExclamationTriangleIcon className="w-6 h-6" />}
            iconBoxClassName={getViolationColor(getViolationRate(summary.avgComplianceRate))}
          />
          <SummaryCard
            variant="elevated"
            title={t('speedComplianceReport.violations')}
            value={summary.totalOverspeeds}
            icon={<ExclamationTriangleIcon className="w-6 h-6 text-red-600" />}
            iconBoxClassName="bg-red-100 dark:bg-red-900/30"
          />
          <SummaryCard
            variant="elevated"
            title="Resor"
            value={summary.totalTrips}
            icon={<TruckIcon className="w-6 h-6 text-blue-600" />}
            iconBoxClassName="bg-BgPrimaryLight"
          />
          <SummaryCard
            variant="elevated"
            title={t('speedComplianceReport.timeOverLimit')}
            value={formatDuration(summary.overspeedDuration)}
            icon={<ClockIcon className="w-6 h-6 text-purple-600" />}
            iconBoxClassName="bg-purple-100 dark:bg-purple-900/30"
          />
        </div>
      )}

      {/* Tabs */}
      {dataLoaded && (
        <>
          <div className="border-b border-border mb-6">
            <div className="flex gap-1">
              {(['overview', 'organizations', 'vehicles', 'segments', 'trend'] as TabType[]).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-2 font-medium text-sm transition-colors ${
                    activeTab === tab
                      ? 'text-bgPrimary border-b-2 border-bgPrimary'
                      : 'text-textSecondary hover:text-textDefault'
                  }`}
                >
                  {tab === 'overview' && 'Översikt'}
                  {tab === 'organizations' && 'Per organisation'}
                  {tab === 'vehicles' && 'Per fordon'}
                  {tab === 'segments' && 'Per hastighetsgräns'}
                  {tab === 'trend' && 'Trend'}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <LoadingState minHeight="h-64" size="md" />
          ) : (
            <>
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="bg-bgElevated rounded-lg border border-border p-6">
                    <h3 className="text-lg font-semibold text-textDefault mb-4">
                      Över hastighetsgräns per organisation
                    </h3>
                    <ResponsiveContainer width="100%" height={Math.max(300, orgStats.length * 35)}>
                      <BarChart 
                        data={orgStats.map(o => ({ 
                          ...o, 
                          violationRate: getViolationRate(o.complianceRate),
                          displayValue: getViolationRate(o.complianceRate) < 1 ? o.totalOverspeeds : getViolationRate(o.complianceRate)
                        }))} 
                        layout="vertical"
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" domain={[0, 'auto']} />
                        <YAxis type="category" dataKey="organizationName" width={150} tick={{ fontSize: 11 }} />
                        <Tooltip 
                          formatter={(_value: number, _name: string, props: any) => {
                            const org = props.payload;
                            return [`${org.totalOverspeeds} överträdelser (${org.violationRate}%)`, 'Hastighetsöverträdelser'];
                          }}
                        />
                        <Bar dataKey="totalOverspeeds" fill="#ef4444" name="Antal överträdelser" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="bg-bgElevated rounded-lg border border-border p-6">
                    <h3 className="text-lg font-semibold text-textDefault mb-4">
                      Flest överträdelser (fordon)
                    </h3>
                    <div className="space-y-3">
                      {vehicleStats.slice(0, 5).map((v, idx) => (
                        <div key={v.vehicleId} className="flex items-center justify-between p-3 bg-bgBase rounded-lg">
                          <div className="flex items-center gap-3">
                            <span className="w-6 h-6 flex items-center justify-center bg-red-100 dark:bg-red-900/30 text-red-600 rounded-full text-sm font-bold">
                              {idx + 1}
                            </span>
                            <div>
                              <p className="font-medium text-textDefault">{v.registrationNumber}</p>
                              <p className="text-sm text-textSecondary">{v.make} {v.model}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-red-600">{v.totalOverspeeds}</p>
                            <p className="text-sm text-textSecondary">överträdelser</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Organizations Tab */}
              {activeTab === 'organizations' && (
                <DataTable<SpeedComplianceStats>
                  data={orgStats}
                  rowKey="organizationId"
                  pageSize={15}
                  columns={[
                    { key: 'organizationName', label: 'Organisation', sortable: true },
                    { key: 'totalTrips', label: 'Resor', align: 'right', sortable: true },
                    { key: 'totalDistance', label: 'Distans', align: 'right', sortable: true, render: (v) => `${Number(v ?? 0)} km` },
                    { key: 'totalPoints', label: 'Punkter', align: 'right', sortable: true, render: (v) => (Number(v ?? 0)).toLocaleString('sv-SE') },
                    { key: 'totalOverspeeds', label: 'Överträdelser', align: 'right', sortable: true, render: (v) => <span className="text-red-600 font-medium">{Number(v ?? 0)}</span> },
                    { key: '_violationRate', label: '%', align: 'right', sortable: true, sortValue: (row) => row.totalPoints > 0 ? Math.round((row.totalOverspeeds / row.totalPoints) * 1000) / 10 : 0, render: (_, row) => { const r = row.totalPoints > 0 ? Math.round((row.totalOverspeeds / row.totalPoints) * 1000) / 10 : 0; return <span className={`px-2 py-1 rounded-full text-sm font-medium ${getViolationColor(r)}`}>{r}%</span>; } },
                  ]}
                  features={{ csvExport: true, search: true, stickyHeader: true }}
                />
              )}

              {/* Vehicles Tab */}
              {activeTab === 'vehicles' && (
                <DataTable<VehicleSpeedStats>
                  data={vehicleStats}
                  rowKey="vehicleId"
                  pageSize={15}
                  columns={[
                    { key: 'registrationNumber', label: 'Fordon', sortable: true, render: (v, row) => <div><p className="font-medium">{String(v ?? '')}</p><p className="text-sm text-textSecondary">{row.make} {row.model}</p></div> },
                    { key: 'organizationName', label: 'Organisation', sortable: true },
                    { key: 'totalDistance', label: 'Distans', align: 'right', sortable: true, render: (v) => `${Number(v ?? 0)} km` },
                    { key: 'totalPoints', label: 'Punkter', align: 'right', sortable: true, render: (v) => (Number(v ?? 0)).toLocaleString('sv-SE') },
                    { key: 'totalOverspeeds', label: 'Överträdelser', align: 'right', sortable: true, render: (v) => <span className="text-red-600 font-medium">{Number(v ?? 0)}</span> },
                    { key: 'maxSpeed', label: 'Max km/h', align: 'right', sortable: true, render: (v) => <span className="font-medium">{Number(v ?? 0)}</span> },
                    { key: '_violationRate', label: '%', align: 'right', sortable: true, sortValue: (row) => row.totalPoints > 0 ? Math.min(100, Math.round((row.totalOverspeeds / row.totalPoints) * 1000) / 10) : 0, render: (_, row) => { const r = row.totalPoints > 0 ? Math.min(100, Math.round((row.totalOverspeeds / row.totalPoints) * 1000) / 10) : 0; return <span className={`px-2 py-1 rounded-full text-sm font-medium ${getViolationColor(r)}`}>{r}%</span>; } },
                  ]}
                  features={{ csvExport: true, search: true, stickyHeader: true }}
                />
              )}

              {/* Segments Tab */}
              {activeTab === 'segments' && (
                <div className="space-y-4">
                  <div className="p-4 bg-bgBase rounded-lg border border-border">
                    <h3 className="text-lg font-semibold text-textDefault">Överträdelser per hastighetsgräns</h3>
                    <p className="text-sm text-textSecondary mt-1">
                      {t('speedComplianceReport.segmentsHint')}
                    </p>
                  </div>
                  <DataTable<SpeedSegment>
                    data={speedSegments}
                    rowKey="speedLimit"
                    emptyMessage="Ingen data för valt datumintervall"
                    columns={[
                      { key: 'speedLimit', label: 'Hastighetsgräns', sortable: true, render: (v) => <div className="flex items-center gap-2"><span className="inline-flex items-center justify-center w-12 h-8 rounded bg-bgContainer text-sm font-bold text-textDefault">{Number(v ?? 0)}</span><span className="text-textSecondary">km/h</span></div> },
                      { key: 'totalPoints', label: 'Mätpunkter', align: 'right', sortable: true, render: (v) => (Number(v ?? 0)).toLocaleString('sv-SE') },
                      { key: 'overspeedPoints', label: 'Överträdelser', align: 'right', sortable: true, render: (v) => <span className="text-red-600 font-medium">{(Number(v ?? 0)).toLocaleString('sv-SE')}</span> },
                      { key: 'violationPct', label: 'Andel (%)', align: 'right', sortable: true, render: (_, row) => <span className={`px-2 py-1 rounded-full text-sm font-medium ${getViolationColor(Number(row.violationPct))}`}>{row.violationPct}%</span> },
                      { key: 'maxSpeed', label: 'Högsta vid överträdelse', align: 'right', sortable: true, render: (v, row) => { const n = Number(v ?? 0); return n > 0 ? <span className={`font-bold ${n > row.speedLimit * 1.1 ? 'text-red-600' : 'text-textDefault'}`}>{n} km/h</span> : <span className="text-textSecondary">-</span>; } },
                      { key: 'maxExcess', label: 'Max över gräns', align: 'right', sortable: true, render: (v) => { const n = Number(v ?? 0); return n > 0 ? <span className="font-bold text-red-600">+{n} km/h</span> : <span className="text-green-600">0</span>; } },
                    ]}
                    features={{ csvExport: true, search: true, stickyHeader: true }}
                  />
                  {speedSegments.length > 0 && segmentsSummary && (
                    <div className="p-4 bg-bgBase border-t border-border">
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div>
                          <p className="text-sm text-textSecondary">Totalt mätpunkter</p>
                          <p className="text-xl font-bold text-textDefault">
                            {segmentsSummary.totalPoints.toLocaleString('sv-SE')}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-textSecondary">Totalt överträdelser</p>
                          <p className="text-xl font-bold text-red-600">
                            {segmentsSummary.overspeedPoints.toLocaleString('sv-SE')}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-textSecondary">Andel överträdelser</p>
                          <p className="text-xl font-bold text-textDefault">
                            {segmentsSummary.violationPct}%
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Trend Tab */}
              {activeTab === 'trend' && (
                <div className="bg-bgElevated rounded-lg border border-border p-6">
                  <h3 className="text-lg font-semibold text-textDefault mb-4">Överträdelser över tid</h3>
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={trendData.map(t => ({ ...t, violationRate: getViolationRate(t.complianceRate) }))}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period" tick={{ fontSize: 12 }} />
                      <YAxis yAxisId="left" domain={[0, 100]} />
                      <YAxis yAxisId="right" orientation="right" />
                      <Tooltip />
                      <Legend />
                      <Line yAxisId="left" type="monotone" dataKey="violationRate" stroke="#ef4444" strokeWidth={2} name="Överträdelser %" />
                      <Line yAxisId="right" type="monotone" dataKey="totalOverspeeds" stroke="#f97316" strokeWidth={2} name="Överträdelser (antal)" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </>
          )}
        </>
      )}
    </ReportPageLayout>
  );
};

export default SpeedComplianceReport;
